import { subject } from '@casl/ability'
import { sizes } from '@lmc/common/constants'
import { type Carer, UserRole } from '@lmc/ts-common'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import { canViewerEditAccessAndPermissionsOnTarget } from 'components/forms/screens/TeamMemberEditForm/utils'
import { AddTeamMemberWizard } from 'components/modals/AddTeamMemberWizard'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Spinner } from '../../../../components/generic'
import useCarers from '../../hooks/useCarers'
import usePagedCarers from '../../hooks/usePagedCarers'
import {
  carersForTab,
  isListableCarer,
  isRoleTabId,
  RoleTabId
} from '../../utils/roleTabs'
import { DEFAULT_SORT, type SortKey, sortCarers } from '../../utils/sort'
import {
  applyTeamFilters,
  DEFAULT_FILTERS,
  EMPTY_FILTERS,
  type TeamFilters
} from '../../utils/teamFilters'
import { TEAM_LIST_QUERY_KEYS } from './constants'
import EditPermissionsPanel from './EditPermissionsPanel'
import RoleTabs from './RoleTabs'
import TeamFiltersPopover from './TeamFiltersPopover'
import TeamListHeaderV2 from './TeamListHeaderV2'
import TeamListItemV2 from './TeamListItemV2'
import TeamListPagination from './TeamListPagination'
import TeamListToolbar from './TeamListToolbar'

const StyledScreen = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 109px);
  background-color: ${(props) => props.theme.color.background.tertiary};
  padding: 24px 0;
  overflow-y: auto;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    min-height: calc(100vh - 63px);
    padding: 16px 0;
  }
`

const StyledContent = styled.div`
  width: 100%;
  max-width: ${sizes.childScreen}px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 0 16px;
    gap: 16px;
  }
`

const StyledMainCard = styled.div`
  background-color: ${(props) => props.theme.palette.white};
  border-radius: 16px;
  overflow: hidden;
`

const StyledList = styled.ul`
  background-color: ${(props) => props.theme.palette.white};
  list-style: none;
  margin: 0;
  padding: 0;
`

const StyledEmpty = styled.div`
  padding: 48px 24px;
`

const matchesName = (carer: Carer, search: string): boolean => {
  if (!search) {
    return true
  }
  const haystack =
    `${carer?.name?.first ?? ''} ${carer?.name?.last ?? ''}`.toLowerCase()
  return haystack.includes(search.toLowerCase())
}

const TeamListV2: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { isLoading, carers, fetchCarers } = useCarers()
  const location = useLocation()
  const navigate = useNavigate()

  const viewerRole = useSelector(
    (state: { global: { user?: { role?: UserRole } } }) =>
      state.global.user?.role
  )

  // IT role has Update User (for profile edits) but should not manage roles/permissions.
  // Update Home is held by Admin and Management only, so use it as the gate here.
  // However, management role should be able to edit permissions even if the backend
  // ability check fails (CM-3762), so also check the viewer's role directly.
  const abilityResult = useAbilityAccess([{ i: 'update', this: 'Home' }])
  const canEditUserPermissions =
    abilityResult ||
    viewerRole === UserRole.careHomeAdmin ||
    viewerRole === UserRole.management
  // Probe against a staff-role invitation, not the bare 'UserInvitation' type:
  // Senior Carers have a conditional `create UserInvitation where role: family`
  // rule, and a bare-type check matches it — so the Add member button showed and
  // then 403'd on a staff invite. A role-scoped subject only passes for roles
  // that can actually invite staff (Admin / Management).
  const canInviteUser = useAbilityAccess([
    { i: 'create', this: subject('UserInvitation', { role: UserRole.carer }) }
  ])

  const tabFromUrl = new URLSearchParams(location.search).get('tab')
  const activeTab: RoleTabId = isRoleTabId(tabFromUrl)
    ? tabFromUrl
    : RoleTabId.AllMembers

  // Clean up unrecognised `?tab=` params from the URL so they don't survive
  // navigation. Runs once per unrecognised value.
  useEffect(() => {
    if (tabFromUrl !== null && !isRoleTabId(tabFromUrl)) {
      const params = new URLSearchParams(location.search)
      params.delete('tab')
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      )
    }
  }, [tabFromUrl, location.pathname, location.search, navigate])

  const [search, setSearch] = useState<string>('')
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT)
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  // The Edit-permissions panel mounts only while a carer is selected; clearing
  // the carer on close discards any unsaved edits so reopening starts fresh.
  const [editCarer, setEditCarer] = useState<Carer | null>(null)
  const [filters, setFilters] = useState<TeamFilters>(DEFAULT_FILTERS)

  // Search is intentionally available only on the 'All members' tab. When the
  // user navigates away, drop any prior query so it doesn't quietly apply to
  // a future return visit (see Figma node 7782:16047).
  useEffect(() => {
    if (activeTab !== RoleTabId.AllMembers && search !== '') {
      setSearch('')
    }
  }, [activeTab, search])

  const handleTabChange = (next: RoleTabId) => {
    const params = new URLSearchParams(location.search)
    if (next === RoleTabId.AllMembers) {
      params.delete('tab')
    } else {
      params.set('tab', next)
    }
    navigate({ pathname: location.pathname, search: params.toString() })
  }

  const listableCarers = useMemo(
    () => (carers ?? []).filter(isListableCarer),
    [carers]
  )

  const filteredCarers = useMemo(() => {
    const byTab = carersForTab(listableCarers, activeTab)
    const effectiveSearch = activeTab === RoleTabId.AllMembers ? search : ''
    const bySearch = byTab.filter((c) => matchesName(c, effectiveSearch))
    const byFilters = applyTeamFilters(bySearch, filters)
    return sortCarers(byFilters, sortKey)
  }, [listableCarers, activeTab, search, sortKey, filters])

  const resetKey = `${activeTab}|${search}|${sortKey}|${filters.statuses.join(
    ','
  )}|${filters.siteIds.join(',')}`
  const { pagedCarers, currentPage, totalPages, setPage } = usePagedCarers(
    filteredCarers,
    resetKey
  )

  const handleAddMember = () => setIsWizardOpen(true)

  const handleEditPermissions = (carer: Carer) => setEditCarer(carer)

  const handleSeeProfile = (carer: Carer) => {
    if (!carer?.id) {
      return
    }
    navigate(`/organisation/team/${carer.id}/profile`)
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <StyledScreen data-testid="team-list-v2">
      <StyledContent>
        <StyledMainCard>
          <TeamListHeaderV2
            canAddMember={canInviteUser}
            onAddMember={handleAddMember}
            testId="team-list-v2-header"
          />
        </StyledMainCard>
        <StyledMainCard>
          <RoleTabs
            activeTab={activeTab}
            onChange={handleTabChange}
            testId="team-list-v2-tabs"
          />
          <TeamListToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchVisible={activeTab === RoleTabId.AllMembers}
            filtersControl={
              <TeamFiltersPopover
                filters={filters}
                onApply={setFilters}
                testId="team-list-v2-toolbar-filters"
              />
            }
            sortKey={sortKey}
            onSortChange={setSortKey}
            activeTab={activeTab}
            count={filteredCarers.length}
            testId="team-list-v2-toolbar"
          />
          {pagedCarers.length ? (
            <StyledList
              role="list"
              aria-label="Team members"
              data-testid="team-list-v2-list"
            >
              {pagedCarers.map((carer) => (
                <TeamListItemV2
                  key={carer.id}
                  testId="team-list-v2-row"
                  carer={carer}
                  canEditPermissions={canEditUserPermissions}
                  canEditTarget={canViewerEditAccessAndPermissionsOnTarget(
                    viewerRole,
                    carer.role as UserRole
                  )}
                  onEditPermissions={handleEditPermissions}
                  onSeeProfile={handleSeeProfile}
                />
              ))}
            </StyledList>
          ) : listableCarers.length === 0 ? (
            <StyledEmpty>
              <EmptyState
                title={t('general.emptyState.noTeamListResponseTitle')}
                description={t(
                  'general.emptyState.noTeamListResponseDescription'
                )}
                icon={<NoDataIcon />}
                testId="team-list-v2-empty"
              />
            </StyledEmpty>
          ) : (
            <StyledEmpty>
              <EmptyState
                title={t('general.emptyState.noTeamMembersMatchTitle')}
                description={t(
                  'general.emptyState.noTeamMembersMatchDescription'
                )}
                icon={<NoDataIcon />}
                testId="team-list-v2-empty"
              />
            </StyledEmpty>
          )}
          <TeamListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            testId="team-list-v2-pagination"
          />
        </StyledMainCard>
      </StyledContent>
      <AddTeamMemberWizard
        open={isWizardOpen}
        onClose={() => {
          setIsWizardOpen(false)
          // Invalidate cache so newly-invited member appears without a manual reload (CM-3785)
          queryClient.invalidateQueries(TEAM_LIST_QUERY_KEYS.CARERS)
        }}
        testId="add-team-member-wizard"
      />
      <EditPermissionsPanel
        open={editCarer !== null}
        carer={editCarer}
        onClose={() => setEditCarer(null)}
        onSaved={fetchCarers}
      />
    </StyledScreen>
  )
}

export default TeamListV2
